import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CheckCircle } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { KYC_STATUS_ONBOARDING_NOT_FINISHED } from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetIdentificationStatus } from "@/services/api";
import {
  IdentificationStatusResponse,
  IdentificationStatusResponseKycStatus,
} from "@/services/model";

import {
  clearAddDeviceOrderSuccessGate,
  hasAddDeviceOrderSuccessGate,
} from "./addDeviceOrderSuccessGate";
import { getAddDeviceSuccessNextStep } from "./getAddDeviceSuccessNextStep";

/** Handoff-local poll while fraud check / unsettled status is in progress. */
export const ADD_DEVICE_SUCCESS_POLL_MS = 1000;
/** Fallback to cockpit if status never leaves ONBOARDING_NOT_FINISHED / empty. */
export const ADD_DEVICE_SUCCESS_UNSETTLED_MAX_WAIT_MS = 30000;

export const AddDeviceSuccess: FC = () => {
  const { t } = useTranslation("portal", { keyPrefix: "add-device.success" });
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (!hasAddDeviceOrderSuccessGate()) {
      navigate(portalRoutes.cockpit.base, { replace: true });
      setHasAccess(false);

      return;
    }

    setHasAccess(true);
  }, [navigate]);

  const leaveHandoff = useCallback(
    (to: string) => {
      if (hasNavigatedRef.current) {
        return;
      }

      hasNavigatedRef.current = true;
      clearAddDeviceOrderSuccessGate();
      navigate(to, { replace: true });
    },
    [navigate],
  );

  const handleSkip = useCallback(() => {
    leaveHandoff(portalRoutes.cockpit.base);
  }, [leaveHandoff]);

  const { data: identificationStatus, isLoading } = useGetIdentificationStatus({
    query: {
      enabled: hasAccess === true,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      retry: false,
      staleTime: 0,
      refetchInterval: (data?: IdentificationStatusResponse) => {
        const status = data?.kycStatus as string | undefined;

        if (
          !status ||
          status === KYC_STATUS_ONBOARDING_NOT_FINISHED ||
          status === IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING
        ) {
          return ADD_DEVICE_SUCCESS_POLL_MS;
        }

        return false;
      },
    },
  });

  const nextStep = getAddDeviceSuccessNextStep(
    identificationStatus?.kycStatus as string | undefined,
    identificationStatus?.isVerified,
  );

  const kycStatus = identificationStatus?.kycStatus as string | undefined;
  const isUnsettled =
    hasAccess === true &&
    (!identificationStatus ||
      !kycStatus ||
      kycStatus === KYC_STATUS_ONBOARDING_NOT_FINISHED);

  useEffect(() => {
    if (!isUnsettled) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      leaveHandoff(portalRoutes.cockpit.base);
    }, ADD_DEVICE_SUCCESS_UNSETTLED_MAX_WAIT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isUnsettled, leaveHandoff]);

  useEffect(() => {
    if (hasAccess !== true || hasNavigatedRef.current) {
      return;
    }

    if (nextStep.type === "wait") {
      return;
    }

    if (nextStep.type === "idCheck") {
      const path = nextStep.fromOrder
        ? `${portalRoutes.cockpit.idCheck}?from=order`
        : portalRoutes.cockpit.idCheck;
      leaveHandoff(path);

      return;
    }

    if (nextStep.type === "personalData") {
      leaveHandoff(portalRoutes.user.personal);

      return;
    }

    leaveHandoff(portalRoutes.cockpit.base);
  }, [hasAccess, leaveHandoff, nextStep]);

  if (hasAccess !== true) {
    return null;
  }

  const showFraudCopy =
    kycStatus === IdentificationStatusResponseKycStatus.FRAUD_CHECK_PENDING;

  return (
    <section className="w-full pb-8" data-testid="add-device-success">
      <CardContainer
        variant="ivory"
        className="flex w-full flex-col items-center gap-6 rounded-lg p-6 text-center"
      >
        <CheckCircle className="size-14 text-foreground-success" />
        <div className="flex flex-col gap-2">
          <h1 className="text-body-base font-semibold text-foreground-primary">
            {t("title")}
          </h1>
          <p className="text-body-base text-foreground-secondary">
            {showFraudCopy ? t("fraudCheckInfo") : t("info")}
          </p>
        </div>

        {(isLoading || nextStep.type === "wait") && <LocalLoadingSpinner />}

        <Button
          dataTestid="add-device-success-skip"
          variant="tertiary"
          onClick={handleSkip}
        >
          {t("skipForLater")}
        </Button>
      </CardContainer>
    </section>
  );
};
